import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  getScoreOperation,
  vehiclesDashboardFleetPanel,
  vehiclesDashboardFleetPanelIRF,
  vehiclesDashboardFleetPanelStatuses,
} from "@/common/services/vehicles.api";
import { useToastContext } from "@/common/components/ui/toast/toast.context";
import { useCompanyStore } from "@/common/states/store/Company";

type ScoreOperation = {
  companyId: string;
  date: string;
  noteOperation: number;
};

type IRFPeriod = {
  activations: number;
  indicator: string | null;
  repossessions: number;
};

type IRFData = {
  monthly: IRFPeriod;
  weekly: IRFPeriod;
};

export type DashboardFleetPanelIRFReturn = {
  data: {
    IRF: IRFData;
  };
  ok: boolean;
};
type MetaData = {
  count?: number;
  avgDays?: number;
  minDays?: number;
  maxDays?: number;
};

export type DashboardFleetPanelStatus = {
  ok: boolean;
  statusDelayed: {
    [key: string]: number | MetaData | undefined;
    metaAvailable?: MetaData;
    metaWithout?: MetaData;
    metaWith?: MetaData;
    available?: number;
    under_preparation?: number;
    "delays-with-treatments"?: number;
    "delays-without-treatments"?: number;
    avgDaysWithoutTreatments?: number;
    avgDaysWithTreatments?: number;
  };
  statusVehicles: Record<string, number>;
  totalVehicles: number;
  contestedMaintenances: number;
  unfinishedMaintenances: number;
  pendingLicensingCount?: number;
};

interface Partner {
  [key: string]: string;
}

export const useGetScoreOperation = (params?: {
  startDate?: string;
  endDate?: string;
}) => {
  const { notifyError } = useToastContext();

  const { data, isFetching, isError, error, refetch } = useQuery<
    ScoreOperation[],
    Error
  >({
    queryKey: [
      "dashboard",
      "score-operation",
      params?.startDate,
      params?.endDate,
    ],
    queryFn: async () => {
      const response = await getScoreOperation(params);
      if (!response?.ok) {
        notifyError("Erro ao buscar dados do score de operação");
        return []; // Retornar array vazio em caso de erro
      }

      // Garantir que sempre retorna um array
      const responseData = response.data;
      if (!Array.isArray(responseData)) {
        console.warn(
          "API retornou dados inválidos (não é array):",
          responseData
        );
        return [];
      }

      return responseData as ScoreOperation[];
    },
    refetchOnWindowFocus: false,
  });

  return {
    data: data || [], // ✅ Usar data e garantir array vazio como fallback
    loading: isFetching,
    isError,
    refetch,
    error,
  };
};

export const useDashboardFleetPanel = (partners: Partner[] | null) => {
  const { selectedCompanies, selectedRegion, selectedUf } = useCompanyStore(
    (state) => ({
    selectedCompanies: state.selectedCompanies,
    selectedRegion: state.selectedRegion,
    selectedUf: state.selectedUf,
    })
  );
  const companyIds = useMemo(
    () => selectedCompanies.map((c) => c.companyId).join(","),
    [selectedCompanies]
  );
  const partnerIds = partners?.map((p) => p.value).join(",");
  const queryKey = useMemo(
    () => [
      "dashboard",
      "fleet-panel",
      companyIds,
      selectedRegion,
      selectedUf,
      partnerIds,
    ],
    [companyIds, selectedRegion, selectedUf, partnerIds]
  );

  const { data, isFetching, isError, error, refetch } = useQuery({
    queryKey,
    queryFn: async () => {
      const response = await vehiclesDashboardFleetPanel(partnerIds);
      if (!response?.ok) {
        throw new Error("Erro ao buscar dados do painel de frota");
      }
      return response ?? {};
    },
    refetchOnWindowFocus: false,
  });

  return {
    data,
    isFetching,
    isError,
    error,
    refetch,
  };
};

export const useDashboardFleetPanelIRF = (partners: Partner[] | null) => {
  const { selectedCompanies, selectedRegion, selectedUf } = useCompanyStore(
    (state) => ({
    selectedCompanies: state.selectedCompanies,
    selectedRegion: state.selectedRegion,
    selectedUf: state.selectedUf,
    })
  );
  const companyIds = useMemo(
    () => selectedCompanies.map((c) => c.companyId).join(","),
    [selectedCompanies]
  );
  const partnerIds = partners?.map((p) => p.value).join(",");

  const queryKey = useMemo(
    () => [
      "dashboard",
      "fleet-panel-irf",
      companyIds,
      selectedRegion,
      selectedUf,
      partnerIds,
    ],
    [companyIds, selectedRegion, selectedUf, partnerIds]
  );

  const { data, isFetching, isError, error, refetch } = useQuery<
    DashboardFleetPanelIRFReturn["data"],
    Error
  >({
    queryKey,
    queryFn: async (): Promise<DashboardFleetPanelIRFReturn["data"]> => {
      const response = await vehiclesDashboardFleetPanelIRF(partnerIds);
      if (!response?.ok) {
        throw new Error("Erro ao buscar dados do painel de frota IRF");
      }
      return response.data as DashboardFleetPanelIRFReturn["data"];
    },
    refetchOnWindowFocus: false,
  });

  return {
    data,
    isFetching,
    refetch,
    isError,
    error,
  };
};

export const useDashboardFleetPanelStatuses = (partners: Partner[] | null) => {
  // <-- 1. Recebe os parceiros
  const { selectedCompanies, selectedRegion, selectedUf } = useCompanyStore(
    (state) => ({
    selectedCompanies: state.selectedCompanies,
    selectedRegion: state.selectedRegion,
    selectedUf: state.selectedUf,
    })
  );
  const companyIds = useMemo(
    () => selectedCompanies.map((c) => c.companyId).join(","),
    [selectedCompanies]
  );
  const partnerIds = partners?.map((p) => p.value).join(","); // <-- 2. Cria os IDs

  const queryKey = useMemo(
    () => [
      "dashboard",
      "fleet-panel-statuses",
      companyIds,
      selectedRegion,
      selectedUf,
      partnerIds,
    ],
    [companyIds, selectedRegion, selectedUf, partnerIds]
  );

  const { data, isFetching, isError, error, refetch } = useQuery<
    DashboardFleetPanelStatus,
    Error
  >({
    queryKey,
    queryFn: async () => {
      const response = await vehiclesDashboardFleetPanelStatuses(partnerIds);
      if (!response?.ok) {
        throw new Error("Erro ao buscar dados do painel de frota IRF");
      }
      return response.data as DashboardFleetPanelStatus;
    },
    refetchOnWindowFocus: false,
  });

  return {
    data,
    isFetching,
    refetch,
    isError,
    error,
  };
};
